import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken, formatApiErrorDetail } from "@/lib/api";
import { LANG, URL_LANG, adoptAccountLang, switchLang } from "@/lib/i18n";

// A ?lang= link is an explicit choice: save it to the account; otherwise follow the account.
const syncLang = async (u, target) => {
  if (URL_LANG || !u.language) {
    if (u.language !== LANG) await api.put("/auth/language", { language: LANG }).catch(() => {});
    return false;
  }
  return adoptAccountLang(u.language, target);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = not authed, object = authed
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (await syncLang(data)) return;
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      if (await syncLang(data.user, data.user.role === "admin" ? "/admin" : "/dashboard")) return { ok: true };
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, user_type = "home") => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, user_type, language: LANG });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const setLanguage = async (lang) => {
    if (user) await api.put("/auth/language", { language: lang }).catch(() => {});
    switchLang(lang);
  };

  const logout = () => {
    clearToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh: fetchMe, setLanguage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
